/**
 * Loading the Google Maps JavaScript API, and proving a key works.
 *
 * Two jobs that look like one but are not, because of a hard limit in the API
 * itself: the Maps script can be loaded ONCE per page, with ONE key, and there
 * is no supported way to unload it and try another. The map view calls
 * `loadGoogleMaps` and lives with the answer. The Settings panel has to try a
 * key the operator has just typed, over and over, so it renders `previewDoc`
 * into a throwaway iframe instead and never touches this page's own loader.
 *
 * ⚠ The preview iframe has to be VISIBLE, and that is not a cosmetic choice.
 * The first version of this hid it off-screen at `left:-9999px` and asked
 * whether `gm_authFailure` had fired. It never did - measured 2026-08-16: an
 * off-screen map loads `main.js`, `map.js` and the low-resolution
 * `StaticMapService.GetMapImage` placeholder, and then stops. It never issues
 * `AuthenticationService.Authenticate`, because Maps defers that until tiles
 * are actually wanted. So the probe waited its two and a half seconds, heard
 * nothing, and reported a plainly invalid key as working. A hidden map cannot
 * test a key. A visible one tests it by being visible.
 */

const SCRIPT_ID = "wa-google-maps-js";
const MAPS_JS = "https://maps.googleapis.com/maps/api/js";

/** Where the preview map opens: Estonia, whole country in frame. */
const PREVIEW_CENTER = { lat: 58.7, lng: 25.2 };
const PREVIEW_ZOOM = 6;

export interface LoadOptions {
  /** Extra libraries beyond the core map. `marker` is the modern pin. */
  libraries?: string[];
  language?: string;
  region?: string;
}

/**
 * `callback` is not optional in practice, and that is the whole subtlety here.
 *
 * With `loading=async` - which Google now warns about omitting - the script's
 * own `onload` fires BEFORE `google.maps` exists, so anything that touches
 * `new google.maps.Map` from an `onload` handler dies on "Map is not a
 * constructor". The named callback is the documented ready signal, and it is
 * the only one that is true when it fires.
 */
function scriptUrl(key: string, o: LoadOptions = {}, callback?: string): string {
  const p = new URLSearchParams({
    key,
    v: "weekly",
    libraries: (o.libraries ?? ["maps", "marker"]).join(","),
    language: o.language ?? "et",
    region: o.region ?? "EE",
    loading: "async",
  });
  if (callback) p.set("callback", callback);
  return `${MAPS_JS}?${p.toString()}`;
}

/** The global the Maps script calls once its core library is usable. */
const READY_CALLBACK = "__waGoogleMapsReady";

let loading: Promise<void> | null = null;
let loadedWith = "";

/**
 * Loads the Maps JavaScript API, once.
 *
 * Repeat calls with the same key share the first promise. A call with a
 * DIFFERENT key rejects rather than quietly using the old one: the second key
 * is the one the caller believes is on screen, and a map drawn with the first
 * would bill and fail against the wrong project with nothing on screen to say
 * so. The page has to reload to change keys, and the caller is told that.
 */
export function loadGoogleMaps(key: string, options: LoadOptions = {}): Promise<void> {
  const trimmed = key.trim();
  if (!trimmed) return Promise.reject(new Error("google_maps_key_missing"));

  if (loading) {
    return loadedWith === trimmed
      ? loading
      : Promise.reject(new Error("google_maps_key_changed_reload_required"));
  }

  loadedWith = trimmed;
  loading = new Promise<void>((resolve, reject) => {
    // Resolved by Google, not by the `<script>` tag: see `scriptUrl`. A page
    // that resolved on `onload` would hand the caller a `google.maps` that is
    // still undefined.
    (window as unknown as Record<string, unknown>)[READY_CALLBACK] = () => resolve();

    const el = document.createElement("script");
    el.id = SCRIPT_ID;
    el.src = scriptUrl(trimmed, options, READY_CALLBACK);
    el.async = true;
    el.onerror = () => reject(new Error("google_maps_script_failed"));
    document.head.appendChild(el);
  });

  return loading;
}


/**
 * Why a preview failed.
 *
 * Short on purpose. There is no `timeout` and no `hidden`: the frame only ever
 * speaks when it has something certain to say, and "nothing happened yet" is
 * not a verdict about the key. See `previewDoc`.
 */
export type MapsTestReason = "auth" | "network" | "script";

/** What the preview frame reports back to the panel that mounted it. */
export type MapProbeResult = { ok: true; ms: number } | { ok: false; reason: MapsTestReason; detail?: string };

/** The field that marks a message as ours, carrying the frame's own token. */
export const PROBE_FIELD = "__waMapsProbe";

/** The wire shape. Flat and all-optional, because it arrives from a frame and
 *  nothing but the token check makes it trustworthy. */
export interface ProbeMessage {
  [PROBE_FIELD]?: string;
  ok?: boolean;
  ms?: number;
  reason?: MapsTestReason;
  detail?: string;
}

/**
 * Reads one `postMessage` and says whether it was this frame's verdict.
 *
 * Kept here beside the document that sends it so the two halves of the protocol
 * cannot drift: the panel imports both or neither.
 */
export function readProbeMessage(event: MessageEvent, token: string): MapProbeResult | null {
  const data = event.data as ProbeMessage | null;
  if (!data || data[PROBE_FIELD] !== token) return null;
  return data.ok
    ? { ok: true, ms: typeof data.ms === "number" ? data.ms : 0 }
    : { ok: false, reason: data.reason ?? "script", detail: data.detail };
}

/**
 * A whole HTML document that draws one map with the given key.
 *
 * Goes into an iframe's `srcdoc`, which inherits this page's origin - so the
 * Referer sent for the Maps script is the CRM's own URL and an HTTP-referrer
 * restriction is tested honestly, rather than being sidestepped by a sandbox
 * with a null origin.
 *
 * ⚠ THE FRAME NEVER GUESSES. It reports a pass only on positive proof that a
 * map drew, a failure only on Google's own error card, and stays silent
 * otherwise. That rule was written after three false passes in one afternoon
 * (2026-08-16), each from a different flavour of "no error appeared, so it must
 * be fine":
 *
 *   - `gm_authFailure`, the documented callback, was never called at all, even
 *     by a frame that visibly failed.
 *   - A `console.error` override installed before the script was never called
 *     either; Maps takes its console reference from a frame of its own.
 *   - A hidden or zero-width frame never requests tiles, so it never
 *     authenticates, so nothing ever goes wrong in it - and a key that Google
 *     rejects on sight was reported as working.
 *   - The error card itself is slow and uneven: sometimes 3 seconds, sometimes
 *     past 10, sometimes not at all on a repeat load. Any deadline that turns
 *     into a pass is therefore a coin toss.
 *
 * What is left is honest: `.gm-err-container` (with `.gm-err-title` and
 * `.gm-err-message`) means refused, tiles or `.gm-style` mean drawn, and if
 * neither ever appears the panel says nothing and lets the operator look at the
 * map, which is the one judge that cannot be fooled.
 */
export function previewDoc(key: string, token: string, stopAfterMs = 60_000): string {
  const src = scriptUrl(key.trim(), { libraries: ["maps"] }, READY_CALLBACK);
  return `<!doctype html><meta charset="utf-8">
<style>html,body{margin:0;height:100%;background:#e9eef5}#m{width:100%;height:100%}</style>
<div id="m"></div>
<script>
var t0 = ${JSON.stringify(Date.now())}, done = false;
function say(m) {
  if (done) return;
  done = true;
  m[${JSON.stringify(PROBE_FIELD)}] = ${JSON.stringify(token)};
  parent.postMessage(m, "*");
}
function errCard() { return document.querySelector(".gm-err-container"); }
function refuse() {
  var msg = document.querySelector(".gm-err-message");
  say({ ok: false, reason: "auth", detail: (msg && msg.textContent || "").trim() });
}
// Positive proof that a map really drew. Tiles are the strongest: nothing but a
// working map fetches them. \`.gm-style\` is the second, for the moment before
// the first tile lands. Deliberately NOT \`maps.gstatic.com\`, which also serves
// the error card's own icon, and not \`StaticMapService\`, which is requested
// even when authentication then fails.
function drew() {
  if (document.querySelector(".gm-style")) return true;
  var e = performance.getEntriesByType("resource");
  for (var i = 0; i < e.length; i++) {
    if (/\\/maps\\/vt|khms?\\d*\\.google/.test(e[i].name)) return true;
  }
  return false;
}
window.gm_authFailure = function () { refuse(); };
window.${READY_CALLBACK} = function () {
  try {
    new google.maps.Map(document.getElementById("m"), {
      center: ${JSON.stringify(PREVIEW_CENTER)},
      zoom: ${PREVIEW_ZOOM},
      disableDefaultUI: true,
      gestureHandling: "none",
      keyboardShortcuts: false
    });
  } catch (e) {
    say({ ok: false, reason: "script", detail: String((e && e.message) || e) });
    return;
  }
  // Runs until one of the two certainties, then stops. The deadline only ends
  // the polling; it never turns into a verdict.
  var deadline = Date.now() + ${stopAfterMs};
  var poll = setInterval(function () {
    if (errCard()) { clearInterval(poll); refuse(); return; }
    if (drew()) { clearInterval(poll); say({ ok: true, ms: Date.now() - t0 }); return; }
    if (Date.now() >= deadline) clearInterval(poll);
  }, 400);
};
var s = document.createElement("script");
s.src = ${JSON.stringify(src)};
s.async = true;
s.onerror = function () { say({ ok: false, reason: "network" }); };
document.head.appendChild(s);
<\/script>`;
}
